/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import classNames from "classnames";
import ContentShell from "components/ContentShell";
import { InputShell } from "components/FormInputShell";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextInput } from "components/FormTextInput";
import { useLoadingOverlay } from "components/LoadingOverlay";
import PlayerCardCanvas from "components/PlayerCardCanvas";
import SearchableInput from "components/SearchableInput";
import { Tabs, Tab, TabPanel } from "components/Tab";
import { useSnackbar } from "hooks/useSnackbar";
import debounce from "lodash.debounce";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  CARD_TYPE_OPTIONS,
  createPlayerCard,
  getManualPlayerCards,
  playerCardInfo,
  updatePlayerCard,
} from "services/playerCardService";
import { getManualPlayerInfo } from "services/playerService";
import sessionHelper from "services/sessionHelper";
import {
  getCountryCodeByName,
  roundOff,
  sendMessageToCanvas,
} from "utils/miscUtils";
import CustomChip from "components/CustomChip";
import SimpleSwitch from "components/Switch";
// import { uploadImageV2 } from "services/uploadService";

const useStyles = makeStyles((theme) => ({
  playerCardCanvas: {
    alignSelf: "center",
    height: 400,
    width: 200,
    padding: "6px 10px 10px 6px",
    borderRadius: "0.35rem",
    backgroundColor: "#dfdfdf",
  },
  hide: {
    display: "hidden",
  },
  playerCardEditor: {
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "center",
    gap: "10px",
    margin: "0 auto",
    padding: "20px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      gap: 25,
    },
    [theme.breakpoints.up("sm")]: {
      gap: 30,
    },
  },
  editorContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    gap: "20px",
    alignItems: "flex-start",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    },
  },
  field: {
    width: 300,
  },
  submitButton: {
    width: "100%",
    backgroundColor: "#1D1030",
    color: "white",
    fontWeight: "bold",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    flex: 1,
    minWidth: 300,
    maxWidth: 450,
  },
  buttonContainer: {
    marginTop: 20,
    width: "100%",
  },
  downloadContainer: {
    marginTop: 20,
    display: "flex",
    flexFlow: "row wrap",
    gap: 10,
  },
  downloadButton: {
    fontSize: 12,
    width: "100%",
    maxWidth: 130,
  },
  green: {
    color: "green",
  },
  red: {
    color: "red",
  },
  chipContainer: {
    display: "flex",
    flexFlow: "row wrap",
    gap: 8,
    overflowY: "auto",
    width: "100%",
    margin: "8px 0",
    maxHeight: 120,
    padding: "4px",
  },
  chipScrollbar: {
    "&::-webkit-scrollbar": {
      width: 6,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#ccc",
      borderRadius: 4,
      "&:hover": {
        backgroundColor: "#999",
      },
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#f1f1f1",
    },
  },
}));

const defaultPageSize = 20,
  defaultPageNo = 1;

let _initialPlayerCard = {
  fullName: "",
  commonNameForename: "",
  commonNameSurname: "",
  displayName: "",
  displayNameAbb: "",
  overallRating: "43",
  positionName: "",
  preferredPosition: "",
  secondaryPosition1: "",
  secondaryPosition2: "",
  cardType: "bronze",
  isDisabled: false,
  clubTextColor: "#DD0000",
  clubBGColor: "#0000FF",
  clubName: "",
  clubId: "",
  clubAbbrName: "",
  clubShortCode: "",
  attackRating: 0,
  defenceRating: 0,
  creativityRating: 0,
  customSellPrice: null, // initially set it to null
  textColor: "#000000",
  positionBGColor: "#FF5733",
  cardBgId: null,
  imageUrl:
    "https://pbf-dev.s3.amazonaws.com/playerJerseys/@1x_webp/Men/JVT/12.webp",
  nationalityImageUrl: "",
  nationality: "Japan",
  shirtNumber: 10,
  playerTags: [],
  cardTextColor: "#000000",
  imageUploadUrl: null,
  playerFaceImage: null as any,
  detImg: null as any,
  rarity: null as any,
  updateId: null as any,
  cardImg: null as any,
  cardImgSm: null as any,
  commonName: null as any,
};

const playerCardTab = 0,
  playerCardDetailsTab = 1;

const PlayerCardEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const [playerTemplates, setPlayerTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [playerDetails, setPlayerDetails] = useState(_initialPlayerCard);
  const [searchLoader, setSearchLoader] = useState(false);
  const [playerTag, setPlayerTag] = useState("");
  const [searchPagination, setSearchPagination] = useState({
    pageNo: defaultPageNo,
    pageSize: defaultPageSize,
    hasMore: false,
  });
  const [manualPlayerInfo, setManualPlayerInfo] = useState({
    playerId: null,
    playerManualInfo: null,
    updateId: null as any,
  });
  const [activeTab, setActiveTab] = useState<number>(playerCardTab);
  const initCountRef = useRef(false);
  // eslint-disable-next-line no-unused-vars
  const [images, setImages] = useState({
    playerCardBg: null,
    cardBg: null,
  });

  const onTabChange = (cTab: any) => {
    // setTab(cTab);
    if (cTab === "tab1") sendToCanvas("INIT", playerDetails);
    else
      sendToCanvas(
        "INIT",
        {
          ...playerDetails,
          rarity: playerDetails.cardType,
          preferredPosition: playerDetails.preferredPosition,
          secondaryPosition1: playerDetails.secondaryPosition1,
          secondaryPosition2: playerDetails.secondaryPosition2,
          defenseRating: playerDetails.defenceRating,
          extraData: {
            clubTextColor: playerDetails.clubTextColor,
            clubBGColor: playerDetails.clubBGColor,
            clubAbbrName: playerDetails.clubAbbrName,
            textColor: playerDetails.textColor,
            positionBGColor: playerDetails.positionBGColor,
          },
        },
        "VIEW_PLAYER_DETAILS",
      );
  };

  const _getManualPlayers = async (options, isFetchMore = false) => {
    setSearchLoader(true);
    try {
      const response = await getManualPlayerCards({
        ...options,
        sortKey: "createdAt",
        sortDir: "DESC",
      });

      setPlayerTemplates((_state) =>
        isFetchMore ? [..._state, ...response.items] : response?.items,
      );
      setSearchLoader(false);
      setSearchPagination((_pageData) => ({
        ..._pageData,
        hasMore: response.morePages,
      }));
    } catch (error) {
      setSearchLoader(false);
      showSnackbar(error.message);
    }
  };

  const _fetchManualPlayerInfo = async (cardId) => {
    showLoadingOverlay();
    try {
      const { data, message } = await getManualPlayerInfo(cardId);
      const _preferredClub = sessionHelper.clubs.find(
        (el) => el.externalClubId === data.clubId,
      );
      let bg = sessionHelper.cardBgs?.find((el) => el?._id === data?.cardBgId);

      if (!bg && !bg?._id) {
        bg = sessionHelper.cardBgs.find(
          (el) => el?.name?.toLowerCase() === data?.rarity,
        );
      }
      if (!bg && !bg?._id) bg = sessionHelper.cardBgs[0];
      const { cardImg, cardImgSm, detImg, textColor, _id } = bg;

      let _playerDetails = {
        ...playerDetails,
        clubId: data?.clubId,
        clubName: _preferredClub?.name,
        club: _preferredClub?.name,
        clubShortCode: _preferredClub?.abbreviation,
        clubTextColor: _preferredClub?.textColor ?? playerDetails.clubTextColor,
        clubBGColor:
          _preferredClub?.backgroundColor ?? playerDetails.clubBGColor,
        clubAbbrName:
          _preferredClub?.abbreviation ?? playerDetails.clubShortCode,
        cardImg,
        cardImgSm,
        detImg,
        cardBgId: _id,
        cardTextColor: textColor,
        preferredPosition: data?.preferredPosition,
        secondaryPosition1: data?.secondaryPosition1,
        secondaryPosition2: data?.secondaryPosition2,
        playerFaceImage: data?.avatar,
        attackRating: data?.attackRating,
        defenceRating: data?.defenseRating,
        creativityRating: data?.creativityRating,
        customSellPrice: data?.customSellPrice,
        displayName: data?.displayName,
        overallRating: data?.overallRating,
        cardType: data?.rarity,
        rarity: data?.rarity,
        displayNameAbb: data?.displayNameAbb,
        imageUrl: data?.imageUrl,
        nationality: data?.nationality,
        nationalityImageUrl: data?.nationalityImageUrl,
        shirtNumber: data?.shirtNumber,
        commonNameForename: data?.commonNameForename,
        commonNameSurname: data?.commonNameSurname,
        commonName: data?.commonName,
      };
      setPlayerDetails((_) => ({ ..._playerDetails }));
      setManualPlayerInfo((__) => ({
        ...__,
        playerManualInfo: { ...data },
      }));
      sendToCanvas("selectedPlayer", { value: _playerDetails });
      showSnackbar(message, "success");
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      hideLoadingOverlay();
    }
  };

  const _fetchMore = async () => {
    try {
      let pageDetails = {
        pageNo: searchPagination.pageNo + 1,
        pageSize: searchPagination.pageSize,
      };
      const response = await getManualPlayerCards({
        ...pageDetails,
        sortKey: "createdAt",
        sortDir: "DESC",
      });

      const filteredData = response.items.filter((v) => {
        const isExist = playerTemplates.find(
          (item) => item?.playerId === v?.playerId,
        );

        if (isExist) return false;
        return true;
      });

      setPlayerTemplates((_state) => [..._state, ...filteredData]);
      setSearchPagination((state) => ({
        ...state,
        ...pageDetails,
        hasMore: response.morePages,
      }));
    } catch (error) {
      showSnackbar(error.message);
    }
  };

  const onSearchChange = async (searchValue, isFetchMore) => {
    setSearchLoader(true);
    try {
      if (searchValue !== "") {
        _getManualPlayers({
          ...searchPagination,
          search: searchValue,
          skipPagination: true,
        });
      }
      setSearchLoader(false);
    } catch (error) {
      setSearchLoader(false);
      showSnackbar(error?.message ?? error);
    }
  };

  const optimizeSearch = debounce(onSearchChange, 300);

  const handlePositionChange = ({ fieldName, value, playerDetState }: any) => {
    let data = playerDetState ? { ...playerDetState } : { ...playerDetails };
    let canvasPayload = {};
    const canvasEventname = "allPositionData";

    const _pos = sessionHelper.positions?.find(
      (el) => el.abbreviationName === value,
    );

    let updatedPosData = {
      [fieldName]: value,
      textColor: _pos?.textColor ?? playerDetails.textColor,
      positionBGColor: _pos?.backgroundColor ?? playerDetails?.positionBGColor,
    };

    data = {
      ...data,
      ...updatedPosData,
    };

    canvasPayload = {
      value: updatedPosData,
    };

    return { data, canvasPayload, canvasEventname };
  };

  const handleCountryChange = ({ fieldName, value, playerDetState }: any) => {
    let data = playerDetState ? { ...playerDetState } : { ...playerDetails };
    let canvasPayload = {};
    const canvasEventname = "nationality";

    const _country = sessionHelper.countries?.find(
      (el) => el?.isoCode === value,
    );

    let updatedCountryData = {
      [fieldName]: _country?.name,
      nationalityImageUrl: _country.imageUrl,
    };

    data = {
      ...data,
      ...updatedCountryData,
    };

    canvasPayload = {
      value: updatedCountryData,
    };

    return { data, canvasPayload, canvasEventname };
  };

  const handleClubChange = ({ fieldName, value, playerDetState }: any) => {
    let data = playerDetState ? { ...playerDetState } : { ...playerDetails };
    let canvasPayload = {};
    const canvasEventname = "allClubData";
    const _club = sessionHelper.clubs?.find(
      (el) => el.externalClubId === value,
    );

    let updatedClubData = {
      [fieldName]: _club?.externalClubId,
      clubName: _club?.name,
      club: _club?.name,
      clubShortCode: _club?.abbreviation,
      clubTextColor: _club?.textColor ?? playerDetails.clubTextColor,
      clubBGColor: _club?.backgroundColor ?? playerDetails.clubBGColor,
      clubAbbrName: _club?.abbreviation ?? playerDetails.clubShortCode,
    };

    data = {
      ...data,
      ...updatedClubData,
    };

    canvasPayload = {
      value: updatedClubData,
    };

    return { data, canvasPayload, canvasEventname };
  };

  const handleNumberFieldChange = ({ fieldName, value }: any) => {
    let data = { ...playerDetails };

    if (value < 1 || value > 99) {
      return {
        data,
        canvasPayload: { value: data[fieldName] },
        canvasEventname: fieldName,
      };
    }

    const updatedValue = {
      [fieldName]: Number(value?.split(".")[0]) ?? "",
    };

    data = {
      ...data,
      ...updatedValue,
    };

    return {
      data,
      canvasPayload: { value: data[fieldName] },
      canvasEventname: fieldName,
    };
  };

  const calculateRarity = (overallRating: any) => {
    if (overallRating < 70) {
      return "bronze";
    } else if (overallRating >= 70 && overallRating <= 79) {
      return "silver";
    } else if (overallRating > 79) {
      return "gold";
    }

    return "bronze";
  };

  const calculatePlayerOvr = async ({
    preferredPosition,
    attackRating = 0,
    defenseRating = 0,
    creativityRating = 0,
  }) => {
    const formula = sessionHelper.ovrFormulas[preferredPosition];

    if (!formula) {
      return 0;
    }

    // Lazy load mathjs only when calculating OVR
    const { evaluate } = await import("mathjs");

    return roundOff(
      evaluate(formula, {
        ATT: attackRating,
        CRE: creativityRating,
        DEF: defenseRating,
      }),
    );
  };

  const onChange = async (event) => {
    let { name, value } = event.target;
    if (name === "customSellPrice" && value === "") value = null; // only take value entered by the admin else set it to null ...
    let data = { ...playerDetails };

    let canvasPayload = { value: value ?? "" };
    let canvasEventname = name;

    const eventMap = {
      preferredPosition: handlePositionChange,
      clubId: handleClubChange,
      nationality: handleCountryChange,
      attackRating: handleNumberFieldChange,
      defenceRating: handleNumberFieldChange,
      creativityRating: handleNumberFieldChange,
    };

    const eventHandler = eventMap[name];

    if (name === "isDisabled") {
      data = {
        ...data,
        isDisabled: !playerDetails.isDisabled,
      };
    } else if (eventHandler) {
      const resp = eventHandler({ fieldName: name, value });

      data = resp.data;

      canvasPayload = resp.canvasPayload;

      canvasEventname = resp.canvasEventname;
    } else if (name === "selectedPlayer") {
      if (!value) {
        canvasPayload = {
          value: {
            ...playerDetails,
          },
        };
        return;
      }

      let _pTemplate = playerTemplates?.find((pt) => pt.playerId === value?.id);
      setSelectedTemplate(_pTemplate); // set the _pTemplate for edit

      let preferredCardBG = sessionHelper.cardBgs.find(
        (el) => el?._id === _pTemplate?.cardBgId,
      );

      if (!preferredCardBG?._id) {
        preferredCardBG = sessionHelper.cardBgs.find(
          (el) => el?.name?.toLowerCase() === _pTemplate?.rarity,
        );
      }

      if (!preferredCardBG?._id) {
        preferredCardBG = { ...sessionHelper.cardBgs[0] };
      }
      const { _id, cardImg, cardImgSm, detImg, textColor } = preferredCardBG;
      data = {
        ...data,
        preferredPosition: _pTemplate?.preferredPosition,
        secondaryPosition1: _pTemplate?.secondaryPosition1,
        secondaryPosition2: _pTemplate?.secondaryPosition2,
        playerFaceImage: _pTemplate?.avatar,
        attackRating: _pTemplate?.attackRating,
        defenceRating: _pTemplate?.defenseRating,
        creativityRating: _pTemplate?.creativityRating,
        customSellPrice: _pTemplate?.customSellPrice,
        displayName: _pTemplate?.displayName,
        overallRating: _pTemplate?.overallRating,
        cardType: _pTemplate?.rarity,
        clubName: _pTemplate?.club,
        clubId: _pTemplate?.clubId,
        rarity: _pTemplate?.rarity,
        displayNameAbb: _pTemplate?.displayNameAbb,
        imageUrl: _pTemplate?.imageUrl,
        nationality: _pTemplate?.nationality,
        nationalityImageUrl: _pTemplate?.nationalityImageUrl,
        clubAbbrName: _pTemplate?.clubShortCode,
        clubShortCode: _pTemplate?.clubShortCode,
        shirtNumber: _pTemplate?.shirtNumber,
        commonNameForename: _pTemplate?.commonNameForename,
        commonNameSurname: _pTemplate?.commonNameSurname,
        commonName: _pTemplate?.commonName,
        cardImg,
        cardImgSm,
        detImg,
        textColor,
        cardBgId: _id,
      };

      const clubChangeResp = handleClubChange({
        fieldName: "clubName",
        value: _pTemplate?.clubId,
        playerDetState: data,
      });
      data = clubChangeResp.data;

      const posChangeResp = handlePositionChange({
        fieldName: "preferredPosition",
        value: _pTemplate?.preferredPosition,
        playerDetState: data,
      });
      data = posChangeResp.data;

      const pos1ChangeResp = handlePositionChange({
        fieldName: "secondaryPosition1",
        value: _pTemplate?.secondaryPosition1,
        playerDetState: data,
      });
      data = pos1ChangeResp.data;

      const pos2ChangeResp = handlePositionChange({
        fieldName: "secondaryPosition2",
        value: _pTemplate?.secondaryPosition2,
        playerDetState: data,
      });
      data = pos2ChangeResp.data;

      const countryChangeResp = handleCountryChange({
        fieldName: "nationality",
        value: getCountryCodeByName(_pTemplate?.nationality),
        playerDetState: data,
      });
      data = countryChangeResp.data;

      canvasPayload = {
        value: {
          ...playerDetails,
          ...data,
          defaultAvatarUrl: _pTemplate?.defaultAvatarUrl,
          fullName: _pTemplate?.fullName ?? playerDetails?.fullName,
          commonNameForename:
            _pTemplate?.commonNameForename ?? playerDetails?.commonNameForename,
          commonNameSurname:
            _pTemplate?.commonNameSurname ?? playerDetails?.commonNameSurname,
          displayName: _pTemplate?.displayName ?? playerDetails?.displayName,
          displayNameAbb: _pTemplate?.displayNameAbb ?? "",
        },
      };
    } else if (name === "updatePlayer") {
      canvasPayload = {
        value: data,
      };
    } else if (name === "cardBg") {
      const bg = sessionHelper.cardBgs?.find((cbg) => cbg._id === value);
      const { cardImg, cardImgSm, detImg, textColor } = bg;
      canvasPayload = {
        value: {
          detImg,
          cardImg,
          cardImgSm,
          cardTextColor: textColor,
        },
      };

      data = {
        ...data,
        detImg,
        cardImg,
        cardImgSm,
        cardTextColor: textColor,
        cardBgId: value,
      };
    } else if (name === "cardType") {
      let preferredCardBG = sessionHelper.cardBgs.find(
        (el) => el?.name?.toLowerCase() === value?.toLowerCase(),
      );

      if (!preferredCardBG?._id) {
        preferredCardBG = { ...sessionHelper.cardBgs[0] };
      }

      const { cardImg, cardImgSm, detImg, textColor, _id } = preferredCardBG;

      const cardBgCanvasData = {
        value: {
          detImg,
          cardImg,
          cardImgSm,
          cardBgId: _id,
          cardTextColor: textColor,
        },
      };

      sendToCanvas("cardBg", cardBgCanvasData);

      data = {
        ...data,
        detImg,
        cardImg,
        cardImgSm,
        cardTextColor: textColor,
        cardBgId: _id,
        [name]: value,
      };
    } else {
      data = {
        ...data,
        [name]: value,
      };
    }

    sendToCanvas(
      canvasEventname === "updatePlayer" ? "selectedPlayer" : canvasEventname,
      canvasPayload,
    );

    const newOvr = await calculatePlayerOvr({
      preferredPosition: data.preferredPosition,
      attackRating: data.attackRating,
      defenseRating: data.defenceRating,
      creativityRating: data.creativityRating,
    });

    if (newOvr !== Number(data.overallRating)) {
      sendToCanvas("overallRating", { value: newOvr });
      data.overallRating = String(newOvr);

      if (data.cardType !== "inForm") {
        data.cardType = calculateRarity(newOvr);
        data.rarity = data.cardType;

        sendToCanvas("cardType", { value: data.cardType });
      }
    }

    setPlayerDetails((prev) => ({ ...prev, ...data }));
  };

  const sendToCanvas = (type: string, data: any, message?: string) => {
    const iframe = document.getElementById("playerCard");

    if (!iframe) {
      console.log("playerCard iframe is not found");
      return;
    }

    sendMessageToCanvas(iframe, {
      message: message ?? "UPDATE_PLAYER",
      type: type === "INIT" ? "INIT" : type,
      data: data,
    });
  };

  const submit = async () => {
    try {
      showLoadingOverlay();
      let response;
      if (id === "create") {
        let payload = {
          template: selectedTemplate?.template ?? selectedTemplate?._id,
          ...playerDetails,
        };

        if (manualPlayerInfo?.playerId) {
          payload["updateId"] = manualPlayerInfo.updateId;
          payload["template"] = manualPlayerInfo.playerManualInfo?.template;
        }

        response = await createPlayerCard(payload);
        setPlayerDetails((prev) => ({
          ...prev,
          miniImageUploadUrl: response?.data?.miniImageUploadUrl,
          imageUploadUrl: response?.data?.imageUploadUrl,
        }));
      } else {
        response = await updatePlayerCard({
          cardId: id,
          ...selectedCard,
          ...playerDetails,
        });

        setPlayerDetails((prev) => ({
          ...prev,
          miniImageUploadUrl: response?.data?.miniImageUploadUrl,
          imageUploadUrl: response?.data?.imageUploadUrl,
        }));
      }

      if (response.data?.imageUploadUrl) {
        sendToCanvas("createPlayerCard", {});
      }

      showSnackbar(response?.message, "success");
      hideLoadingOverlay();

      // navigate(configData.routes.playerDetails.playerCards);
    } catch (error) {
      hideLoadingOverlay();
      showSnackbar(error.message, "error");
    }
  };

  const getCardInfo = async () => {
    showLoadingOverlay();
    let details;
    try {
      const cardInfo = await playerCardInfo(id);
      const _preferredClub = sessionHelper.clubs.find(
        (el) => el.externalClubId === cardInfo.clubId,
      );

      const { clubShortCode: _clubShortCode, preferredPosition } = cardInfo;

      if (!_preferredClub) {
        showSnackbar(
          `Couldn't find club - '${_clubShortCode}' from the club list`,
          "error",
        );
      }

      let bg = sessionHelper.cardBgs?.find(
        (el) => el?._id === cardInfo?.cardBgId,
      );

      if (!bg) {
        bg = sessionHelper
          .getCardBgOptions()
          .find((el) => el?.label === cardInfo?.rarity);
      }
      const { cardImg, cardImgSm, detImg, textColor } = bg;

      details = {
        ...playerDetails,
        ...cardInfo?.extraData,
        clubId: cardInfo?.clubId,
        clubName: _preferredClub?.name,
        club: _preferredClub?.name,
        clubShortCode: _preferredClub?.abbreviation,
        clubTextColor: _preferredClub?.textColor ?? playerDetails.clubTextColor,
        clubBGColor:
          _preferredClub?.backgroundColor ?? playerDetails.clubBGColor,
        clubAbbrName:
          _preferredClub?.abbreviation ?? playerDetails.clubShortCode,
        preferredPosition,
        cardType: cardInfo?.rarity,
        isDisabled: cardInfo?.isDisabled,
        nationality: cardInfo?.nationality,
        attackRating: cardInfo?.attackRating,
        creativityRating: cardInfo?.creativityRating,
        customSellPrice: cardInfo?.customSellPrice,
        defenceRating: cardInfo?.defenseRating,
        overallRating: cardInfo?.overallRating,
        defaultAvatarUrl: cardInfo?.defaultAvatarUrl,
        fullName: cardInfo?.fullName,
        commonNameForename: cardInfo?.commonNameForename,
        commonNameSurname: cardInfo?.commonNameSurname,
        displayName: cardInfo?.displayName,
        displayNameAbb: cardInfo?.displayNameAbb,
        playerTags: cardInfo.tags,
        cardBgId: cardInfo.cardBgId,
        cardImg,
        cardImgSm,
        detImg,
        cardTextColor: textColor,
        imageUrl: cardInfo?.imageUrl,
        nationalityImageUrl: cardInfo.nationalityImageUrl,
        secondaryPosition1: cardInfo?.secondaryPosition1,
        secondaryPosition2: cardInfo?.secondaryPosition2,
      };

      if (!details.detImg) details["detImg"] = detImg;
      if (!details.cardImg) details["cardImg"] = cardImg;
      if (!details.cardImgSm) details["cardImgSm"] = cardImgSm;
      if (!details.cardTextColor) details["cardTextColor"] = textColor;

      setSelectedCard(cardInfo);
      setPlayerDetails((prev) => ({ ...prev, ...details }));

      setTimeout(() => {
        sendToCanvas("selectedPlayer", {
          value: { ...details, playerFaceImage: details.defaultAvatarUrl },
        });
      }, 100);
    } catch (error) {
      showSnackbar(error?.details ?? error?.message ?? error, "error");
    } finally {
      hideLoadingOverlay();
    }
  };

  const handleCanvasEvents = useCallback(
    async (event) => {
      // console.log("initcount", event?.data?.messageName, playerDetails);
      if (event?.data?.messageName === "init") {
        if (id !== "create" && !initCountRef.current) {
          await getCardInfo();
          initCountRef.current = true;
        }

        const _details = Object.fromEntries(searchParams.entries());
        if (_details?.playerId && !initCountRef.current) {
          _fetchManualPlayerInfo(_details?.playerId);
          setManualPlayerInfo((st) => ({ ...st, ..._details }));
          initCountRef.current = true;
        }
      } else if (event?.data?.messageName === "CanvasImageData") {
        setImages((prev) => ({
          ...prev,
          playerCardBg: event.data.value[0],
          cardBg: event.data.value[1],
        }));
      }
    },
    [id],
  );

  // const uploadImage = async ({ data, imageUploadUrl }) => {
  //   try {
  //     if (data.cardBg && imageUploadUrl) {
  //       const blob = data.cardBg;
  //       // Convert blob to file with proper extension
  //       const file = new File([blob], "player-image.webp", {
  //         type: "image/webp",
  //       });

  //       await uploadImageV2({
  //         data: file,
  //         uploadUrl: imageUploadUrl,
  //         resize: true,
  //         resizeToWidth: 832,
  //         resizeToHeight: 1280,
  //       });

  //       showSnackbar("Image Uploaded", "success");
  //     }
  //   } catch (error) {
  //     console.log({ error });
  //     showSnackbar("Error uploading image", "error");
  //   }
  // };

  const handleAddTag = (event: any) => {
    if ((event.key === "Enter" || event.key === ",") && playerTag.trim()) {
      if (!playerDetails.playerTags.includes(playerTag.trim())) {
        setPlayerDetails((state) => ({
          ...state,
          playerTags: [...state.playerTags, playerTag.trim()],
        }));
      }
      setPlayerTag("");
    }
  };

  const onPlayerTagChange = (e: any) => {
    const { value } = e.target;
    value !== "," && setPlayerTag(value);
  };

  const handleDeleteTag = (tagToDelete: any) => {
    setPlayerDetails((state) => ({
      ...state,
      playerTags: state.playerTags.filter((tag) => tag !== tagToDelete),
    }));
  };

  const getDeltaValue = (attackRating: any) => {
    let _value = Number(manualPlayerInfo[attackRating] ?? 0);
    if (!_value) return null;
    return (
      <div>
        <span>δ </span>
        <span
          className={classNames({
            [styles.green]: _value > 0,
            [styles.red]: _value < 0,
          })}
        >
          {` ${_value > 0 ? "+" : ""}${_value}`}
        </span>
      </div>
    );
  };

  useEffect(() => {
    if (id === "create") _getManualPlayers(searchPagination);
    window.addEventListener("message", handleCanvasEvents);
    return () => window.removeEventListener("message", handleCanvasEvents);
  }, [handleCanvasEvents]);

  // useEffect(() => {
  //   setTimeout(
  //     () =>
  //       uploadImage({ data: images, imageUploadUrl: playerDetails.imageUploadUrl }),
  //     0
  //   );
  // }, [images.playerCardBg, images.cardBg]);

  const customStyle = {
    height: 30,
  };

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={() => navigate(-1)}
      title={`${id === "create" ? "Create" : "Edit"} Player Details`}
    >
      <Box className={styles.playerCardEditor}>
        <Box
          style={{
            display: "flex",
            flexFlow: "column wrap",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box className={styles.field}>
            {id === "create" && !manualPlayerInfo.playerId && (
              <SearchableInput
                name="selectedPlayer"
                options={playerTemplates?.map((el) => ({
                  id: el.playerId,
                  label: el.label,
                }))}
                label="Search"
                placeholder="Type to search..."
                onInputChange={optimizeSearch}
                onOptionSelect={onChange}
                customStyle={{ width: "100%" }}
                loading={searchLoader}
                fetchMore={_fetchMore}
                hasMore={searchPagination.hasMore}
              />
            )}
          </Box>
          <br />
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => {
              setActiveTab(newValue);
              onTabChange(newValue === playerCardTab ? "tab1" : "tab2");
            }}
          >
            <Tab label="Player Card" value={playerCardTab} />
            <Tab label="Player Card Details" value={playerCardDetailsTab} />
          </Tabs>
          <TabPanel value={activeTab} index={0}>
            <PlayerCardCanvas
              playerDetails={playerDetails}
              sendToCanvas={sendToCanvas}
              classes={{ marginLeft: 12 }}
            />
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <PlayerCardCanvas
              classes={{ marginLeft: 12 }}
              playerDetails={playerDetails}
              sendToCanvas={(init, data) => {
                sendToCanvas(
                  init,
                  {
                    ...playerDetails,
                    rarity: playerDetails.cardType,
                    preferredPosition: playerDetails.preferredPosition,
                    secondaryPosition1: playerDetails.secondaryPosition1,
                    secondaryPosition2: playerDetails.secondaryPosition2,
                    defenseRating: playerDetails.defenceRating,
                    extraData: {
                      clubTextColor: playerDetails.clubTextColor,
                      clubBGColor: playerDetails.clubBGColor,
                      clubAbbrName: playerDetails.clubAbbrName,
                      textColor: playerDetails.textColor,
                      positionBGColor: playerDetails.positionBGColor,
                    },
                  },
                  "VIEW_PLAYER_DETAILS",
                );
              }}
            />
          </TabPanel>
        </Box>

        <Box style={{ display: "flex", flexFlow: "column" }}>
          <Box className={styles.editorContainer}>
            <Box className={styles.formContainer}>
              <SimpleTextInput
                type="text"
                label="Display Name"
                name="displayName"
                value={playerDetails.displayName}
                customStyle={customStyle}
                onChange={onChange}
              />
              <SimpleTextInput
                type="text"
                label="First name"
                name="commonNameForename"
                value={playerDetails.commonNameForename}
                customStyle={customStyle}
                onChange={onChange}
              />
              <SimpleTextInput
                type="text"
                label="Surname"
                name="commonNameSurname"
                value={playerDetails.commonNameSurname}
                customStyle={customStyle}
                onChange={onChange}
              />
              <SimpleSelectInput
                name="cardType"
                label="Choose Rarity"
                options={CARD_TYPE_OPTIONS}
                value={playerDetails.cardType || ""}
                customStyle={customStyle}
                onChange={onChange}
              />
              <SimpleSelectInput
                name="cardBg"
                label="Choose Card BG"
                options={sessionHelper.getCardBgOptions() ?? []}
                value={playerDetails.cardBgId || ""}
                customStyle={customStyle}
                onChange={onChange}
              />
              <SimpleSelectInput
                name="preferredPosition"
                label="Primary Position"
                options={sessionHelper.positionsOptions}
                value={playerDetails.preferredPosition}
                customStyle={customStyle}
                onChange={onChange}
              />
              <SimpleSelectInput
                name="secondaryPosition1"
                label="Secondary Position1"
                options={sessionHelper.positionsOptions}
                value={playerDetails.secondaryPosition1}
                customStyle={customStyle}
                onChange={onChange}
              />
              <SimpleSelectInput
                name="secondaryPosition2"
                label="Secondary Position2"
                options={sessionHelper.positionsOptions}
                value={playerDetails.secondaryPosition2}
                customStyle={customStyle}
                onChange={onChange}
              />
              <SimpleSwitch
                legendText={"Status"}
                name="isDisabled"
                value={!playerDetails.isDisabled}
                label={playerDetails.isDisabled ? "Inactive" : "Active"}
                onChange={onChange}
              />
            </Box>
            <Box className={styles.formContainer}>
              <SimpleSelectInput
                name="clubId"
                label="Choose Club"
                value={playerDetails.clubId}
                options={sessionHelper.clubsOptions}
                customStyle={customStyle}
                onChange={onChange}
              />
              <SimpleSelectInput
                name="nationality"
                label="Choose nationality"
                options={sessionHelper.countryOptions}
                value={getCountryCodeByName(playerDetails.nationality)}
                customStyle={customStyle}
                onChange={onChange}
              />
              <SimpleTextInput
                type="number"
                label="Attack Points"
                name="attackRating"
                value={playerDetails.attackRating}
                onChange={onChange}
                customStyle={customStyle}
                adornment={getDeltaValue("attackRating")}
              />

              <SimpleTextInput
                type="number"
                label="Defence Points"
                name="defenceRating"
                value={playerDetails.defenceRating}
                onChange={onChange}
                customStyle={customStyle}
                adornment={getDeltaValue("defenceRating")}
              />
              <SimpleTextInput
                type="number"
                label="Creativity Points"
                name="creativityRating"
                value={playerDetails.creativityRating}
                onChange={onChange}
                customStyle={customStyle}
                adornment={getDeltaValue("creativityRating")}
              />
              <SimpleTextInput
                type="number"
                label="Custom Sell Price"
                name="customSellPrice"
                value={playerDetails.customSellPrice}
                onChange={onChange}
                customStyle={customStyle}
                adornment={getDeltaValue("customSellPrice")}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  minWidth: 180,
                }}
              >
                <InputShell
                  label="Player Tags (Press Comma or Enter to add)"
                  name="playerTags"
                >
                  <TextField
                    name="playerTags"
                    variant="outlined"
                    size="small"
                    placeholder="Add Player Tags"
                    value={playerTag}
                    onChange={onPlayerTagChange}
                    onKeyDown={handleAddTag}
                    fullWidth
                    inputProps={{
                      style: {
                        height: 12,
                      },
                    }}
                  />
                </InputShell>
                <Box
                  className={classNames(
                    styles.chipContainer,
                    styles.chipScrollbar,
                  )}
                >
                  {playerDetails.playerTags.map((tag, index) => (
                    <CustomChip
                      key={index}
                      label={tag}
                      onDelete={() => handleDeleteTag(tag)}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
          <Box className={styles.buttonContainer}>
            <Button
              type="submit"
              onClick={submit}
              variant="contained"
              className={styles.submitButton}
              disabled={
                manualPlayerInfo.playerId
                  ? false
                  : !selectedCard && !selectedTemplate
              }
            >
              {id === "create"
                ? manualPlayerInfo.playerId
                  ? "Read & Create "
                  : "Create "
                : "Update "}
              Player Card
            </Button>
          </Box>
        </Box>
      </Box>
    </ContentShell>
  );
};

export default PlayerCardEditor;
